/* Modal de evaluación post-evento.
 *
 * Comportamiento (espejo del EvaluacionPendienteModal de capacitación):
 * - Al iniciar sesión consulta /eventos/evaluacion/pendiente. Si el usuario
 *   estuvo PRESENTE en un evento finalizado que requiere evaluación y no la
 *   completó, abre el modal (se puede SALTAR).
 * - Cuando el usuario entra a /salarios, /historial-de-trabajo o /reclamos y
 *   tiene una evaluación pendiente, el padre activa `bloqueado`: el botón
 *   "Saltar" redirige al inicio (no puede acceder a esas secciones).
 * - Soporta preguntas de OPCIÓN MÚLTIPLE (radio) y ABIERTA (texto).
 */
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonRadio,
  IonRadioGroup,
  IonTextarea,
  useIonToast,
} from "@ionic/react";
import { CircularProgress, Typography } from "@material-ui/core";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Eventos, { Pregunta } from "src/services/eventos";

interface Props {
  isAuth: boolean;
  onPendienteChange?: (pendiente: number) => void; // 0 | 1
  bloqueado?: boolean;
  solicitudReapertura?: number;
}

const EventoEvaluacionModal: React.FC<Props> = ({
  isAuth,
  onPendienteChange,
  bloqueado = false,
  solicitudReapertura = 0,
}) => {
  const [present] = useIonToast();
  const history = useHistory();

  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [evento, setEvento] = useState<any | null>(null);
  const [preguntas, setPreguntas] = useState<Pregunta[]>([]);
  // Respuestas: { [preguntaId]: { opcion_id?, texto? } }
  const [respuestas, setRespuestas] = useState<Record<number, { opcion_id?: number; texto?: string }>>({});

  const yaVerificado = useRef(false);

  const setPendiente = (n: number) => onPendienteChange?.(n);

  const cargarPendiente = async () => {
    setCargando(true);
    try {
      const { bloqueado: hayPendiente, evento: ev } = await Eventos.evaluacionPendiente();
      setPendiente(hayPendiente ? 1 : 0);
      if (!hayPendiente || !ev) {
        setAbierto(false);
        return;
      }
      const detalle = await Eventos.obtenerEvaluacion(ev.evento_id);
      setEvento(detalle.evento || ev);
      setPreguntas(detalle.preguntas || []);
      setRespuestas({});
      setAbierto(true);
    } catch {
      // silencioso: ante error no bloqueamos
      setPendiente(0);
    } finally {
      setCargando(false);
    }
  };

  // Verificación inicial al autenticarse.
  useEffect(() => {
    if (!isAuth) {
      yaVerificado.current = false;
      setAbierto(false);
      setEvento(null);
      setPendiente(0);
      return;
    }
    if (yaVerificado.current) return;
    yaVerificado.current = true;
    cargarPendiente();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth]);

  // Reapertura forzada al entrar a una ruta bloqueada.
  useEffect(() => {
    if (!isAuth || solicitudReapertura === 0) return;
    if (abierto) return;
    cargarPendiente();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitudReapertura]);

  const setOpcion = (pid: number, opcionId: number) =>
    setRespuestas((prev) => ({ ...prev, [pid]: { opcion_id: opcionId } }));

  const setTexto = (pid: number, texto: string) =>
    setRespuestas((prev) => ({ ...prev, [pid]: { texto } }));

  const handleSaltar = () => {
    setAbierto(false);
    if (bloqueado) history.replace("/home");
  };

  const handleEnviar = async () => {
    if (!evento) return;
    // Validar obligatorias
    const faltan = preguntas.filter((p) => {
      if (!p.obligatoria) return false;
      const r = respuestas[Number(p.id)];
      if (!r) return true;
      if (p.tipo === "OPCION_MULTIPLE") return r.opcion_id == null;
      return !((r.texto || "").trim());
    });
    if (faltan.length > 0) {
      present({ message: "Debés responder todas las preguntas obligatorias", duration: 3000, color: "warning" });
      return;
    }

    const payload = preguntas
      .map((p) => {
        const r = respuestas[Number(p.id)];
        if (!r) return null;
        return { pregunta_id: Number(p.id), opcion_id: r.opcion_id ?? null, texto: r.texto ?? "" };
      })
      .filter(Boolean) as Array<{ pregunta_id: number; opcion_id?: number | null; texto?: string }>;

    setGuardando(true);
    try {
      await Eventos.enviarEvaluacion(evento.id || evento.evento_id, payload);
      present({ message: "¡Gracias por tu evaluación!", duration: 3000, color: "success" });
      setPendiente(0);
      setAbierto(false);
    } catch (err: any) {
      present({
        message: err?.response?.data?.error || "No se pudo guardar la evaluación",
        duration: 4000,
        color: "danger",
      });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <IonModal isOpen={abierto} backdropDismiss={false}>
      <IonHeader>
        <IonToolbar>
          <IonTitle style={{ fontSize: 16 }}>Evaluación del evento</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleSaltar} disabled={guardando}>
              Saltar
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div style={{ padding: "12px 16px 4px" }}>
          {evento && (
            <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
              Evento: <strong>{evento.tema}</strong>
            </Typography>
          )}
          {bloqueado && (
            <Typography variant="caption" style={{ color: "#c62828", display: "block", marginTop: 4 }}>
              Para acceder a recibos / historial / reclamos de horas debés completar esta evaluación.
              Si saltás, volverás al inicio.
            </Typography>
          )}
        </div>

        {cargando ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <CircularProgress />
          </div>
        ) : (
          <div style={{ padding: 16 }}>
            {preguntas.map((p, idx) => (
              <div key={p.id} style={{ marginBottom: 24 }}>
                <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 600 }}>
                  {idx + 1}. {p.texto} {p.obligatoria ? "*" : ""}
                </Typography>

                {p.tipo === "OPCION_MULTIPLE" ? (
                  <IonList style={{ margin: "0 -16px" }}>
                    <IonRadioGroup
                      value={respuestas[Number(p.id)]?.opcion_id ?? null}
                      onIonChange={(e) => setOpcion(Number(p.id), Number(e.detail.value))}
                    >
                      {(p.opciones || []).map((o) => (
                        <IonItem key={o.id}>
                          <IonLabel style={{ whiteSpace: "normal" }}>{o.texto}</IonLabel>
                          <IonRadio slot="start" value={o.id} />
                        </IonItem>
                      ))}
                    </IonRadioGroup>
                  </IonList>
                ) : (
                  <IonTextarea
                    placeholder="Escribí tu respuesta..."
                    autoGrow
                    rows={5}
                    value={respuestas[Number(p.id)]?.texto ?? ""}
                    onIonChange={(e) => setTexto(Number(p.id), e.detail.value || "")}
                    style={{
                      border: "1px solid #c4c4c4",
                      borderRadius: 6,
                      padding: "8px 10px",
                      minHeight: 120,
                      background: "#fff",
                    }}
                  />
                )}
              </div>
            ))}

            <IonButton expand="block" onClick={handleEnviar} disabled={guardando} style={{ marginTop: 8 }}>
              {guardando ? "Guardando..." : "Enviar evaluación"}
            </IonButton>
          </div>
        )}
      </IonContent>
    </IonModal>
  );
};

export default EventoEvaluacionModal;
